/**
 * Keyboard shortcuts help overlay.
 *
 * Press "?" anywhere outside a text input to open. Reads from
 * SHORTCUT_REGISTRY so adding a new shortcut there automatically
 * shows up here — no duplicate source of truth.
 *
 * The overlay uses a plain <dialog>-style Radix dialog instead of
 * the GradientDialog because it's an informational surface, not a
 * task with an accent color.
 */

import { useEffect, useState } from "react";
import { Keyboard } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@pureid/ui";
import { SHORTCUT_REGISTRY, type ShortcutDef } from "./registry";

const isMac =
  typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);

export function ShortcutsOverlay() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    function handler(e: KeyboardEvent) {
      if (e.key !== "?") return;
      // Ignore if the user is typing into a field. We check against
      // the event target rather than document.activeElement so this
      // works reliably inside shadow roots / portals.
      const target = e.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA" || target.isContentEditable) {
          return;
        }
      }
      e.preventDefault();
      setOpen((prev) => !prev);
    }
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const groups = groupByCategory(SHORTCUT_REGISTRY);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Keyboard className="h-4 w-4" /> Keyboard shortcuts
          </DialogTitle>
          <DialogDescription>
            Press any shortcut below — or press{" "}
            <kbd className={kbdClass}>?</kbd> again to close.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {groups.map(([group, items]) => (
            <div key={group}>
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
                {group}
              </p>
              <ul className="space-y-1.5">
                {items.map((s) => (
                  <li
                    key={s.label}
                    className="flex items-center justify-between gap-4 text-sm"
                  >
                    <span className="text-foreground">{s.label}</span>
                    <span className="flex items-center gap-1 shrink-0">
                      {renderKeys(s)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

const kbdClass =
  "inline-flex h-5 min-w-[20px] items-center justify-center rounded border border-border/60 bg-muted/40 px-1.5 text-xs font-mono text-muted-foreground";

function renderKeys(def: ShortcutDef) {
  const str = !isMac && def.nonMacKeys ? def.nonMacKeys : def.keys;
  return str.split(" ").map((part, idx) => (
    <kbd key={idx} className={kbdClass}>
      {part}
    </kbd>
  ));
}

function groupByCategory(
  registry: readonly ShortcutDef[],
): Array<[string, readonly ShortcutDef[]]> {
  const map = new Map<string, ShortcutDef[]>();
  for (const s of registry) {
    const bucket = map.get(s.group) ?? [];
    bucket.push(s);
    map.set(s.group, bucket);
  }
  return Array.from(map.entries());
}
